import { Col, Form, Input, Row, Select, Typography } from "antd"
import FormItem from "antd/es/form/FormItem"
import { useContext } from "react"
import { getPlanOptions } from "../../../domain/plans/plan"
import { AuthenticationContext } from "../../../AuthenticationContext"
import LabeledSwitch from "../../common/LabeledSwitch"
import VSpace from "../../common/VSpace"
import ApplyCustomerDisplayAsButton from "../ApplyCustomerDisplayAsButton"
import TenantsContext from "../TenantsContext"
import { useTenantTimeZonesQuery } from "../tenantsQueries"
import { EMAIL_REGEX, isEstimateLimitValid, parseEstimateLimitInput, planHasEstimateLimit } from '../tenantsModel'

const { Text } = Typography

const TenantInfoSection = () => {
  const { state: { tenant }, dispatch, isTenantDataLoading, isSaveButtonDisabled, savedPlanName } = useContext(TenantsContext)
  const { currentUser } = useContext(AuthenticationContext)

  const timeZonesQuery = useTenantTimeZonesQuery()
  const timeZones = timeZonesQuery.data?.timeZones.map(x => ({ label: x.text, value: x.value })) || []

  return <section>
    {
      currentUser?.isSuperUser && <Row style={{ paddingBottom: 20 }}>
        <LabeledSwitch 
          label="This account is active" 
          value={tenant.active} 
          onChange={(value) => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'active', payload: value})}
          disabled={currentUser.tenant.id === tenant.id}
        />
      </Row>
    }
    <Row gutter={24}>
      <Col span={18}>
        <Form.Item label="Company Name">
          <Input
            value={tenant.name}
            onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'name', payload: e.target.value })}
          />
          {
            !tenant.name && <Text type="danger">Company Name is required</Text>
          }
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item label="Email Address">
          <Input value={tenant.email} onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'email', payload: e.target.value })} />
          {
            !tenant.email && <Text type="danger">Email Address is required</Text>
          }
          {
            tenant.email && !tenant.email.match(EMAIL_REGEX) && <Text type="danger">Invalid email address</Text>
          }
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={24}>
      <Col span={18}>
        <Form.Item label="Review Website Address">
          <Input
            value={tenant.reviewWebsiteAddress}
            onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'reviewWebsiteAddress', payload: e.target.value })}
            maxLength={250}
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item label="Office Phone Number">
          <Input
            value={tenant.phoneNumber}
            onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'phoneNumber', payload: e.target.value })}
          />
          {
            !tenant.phoneNumber && <Text type="danger">Phone Number is required</Text>
          }
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={24}>
      <Col span={9}>
        <Form.Item label="Timezone">
          <Select
            value={tenant.timeZoneId}
            options={timeZones}
            onChange={value => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'timeZoneId', payload: value })}
          />
        </Form.Item>
      </Col>
      <Col span={9}>
        <Form.Item label="Address">
          <Input
            value={tenant.address}
            onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'address', payload: e.target.value })}
          />
        </Form.Item>
      </Col>
    </Row>
    <VSpace>
      <LabeledSwitch
        label="Show Success Checklist on Dashboard"
        value={tenant.showSuccessChecklist}
        onChange={value => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'showSuccessChecklist', payload: value })}
      />
      <LabeledSwitch
        label="Allow foremen to see other foremen's jobs in the mobile app"
        value={tenant.allowForemenToSeeOtherForemen}
        onChange={value => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'allowForemenToSeeOtherForemen', payload: value })}
      />
      <Row>
        <Col span={6}>
          <FormItem label="Customer Display As Default">
            <Select
              value={tenant.customerDisplayAsDefault}
              options={[
                { label: 'Default', value: 0 },
                { label: 'First Last Name', value: 1 },
                { label: 'Company', value: 2 },
                { label: 'Last, First Name', value: 3 },
                { label: 'Company, First Last', value: 4 },
                { label: 'Company, Last, First', value: 5 },
              ]}
              onChange={value => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'customerDisplayAsDefault', payload: value })}
              loading={isTenantDataLoading}
            />
          </FormItem>
        </Col>
      </Row>
      <ApplyCustomerDisplayAsButton disabled={isTenantDataLoading || !isSaveButtonDisabled} />
    </VSpace>
    {
      currentUser?.isSuperUser && <Row gutter={24} style={{ paddingTop: 20 }}>
        <Col span={6}>
          <Form.Item label="Plan">
            <Select
              value={tenant.planName}
              options={getPlanOptions(savedPlanName)}
              onChange={value => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'planName', payload: value })}
            />
            {
              !tenant.planName && <Text type="danger">Plan is required</Text>
            }
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="User Count">
            <Input
              type="number"
              min={0}
              value={tenant.userCount ?? 0}
              onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'userCount', payload: e.target.value === '0' ? null : Number.parseInt(e.target.value) })}
            />
          </Form.Item>
        </Col>
        {
          planHasEstimateLimit(tenant.planName) && <Col span={4}>
            <Form.Item label="Estimate Limit">
              <Input
                type="number"
                min={0}
                placeholder="Unlimited"
                value={tenant.estimateLimit ?? ''}
                onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'estimateLimit', payload: parseEstimateLimitInput(e.target.value) })}
              />
              {
                !isEstimateLimitValid(tenant.estimateLimit) && <Text type="danger">Must be 0 or more</Text>
              }
            </Form.Item>
          </Col>
        }
        <Col span={6}>
          <Form.Item label="Stripe Suscription ID">
            <Input
              value={tenant.stripeSubscriptionId}
              onChange={e => dispatch({ type: 'SET_TENANT_FIELD', fieldName: 'stripeSubscriptionId', payload: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Tenant ID">
            {tenant.id}
          </Form.Item>
        </Col>
      </Row>
    }
  </section>

}

export default TenantInfoSection